
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { Helmet } from "react-helmet";
import { useAnalytics } from "@/hooks/useAnalytics";
import { 
  getSortterRedirectUrl,
  getSamblaRedirectUrl, 
  getAxoRedirectUrl,
  ApplicationProvider 
} from "@/services/waterfall/applicationWaterfall";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { sanitizeString, validateSession } from "@/utils/security";

const SortterPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [isRedirecting, setIsRedirecting] = useState<boolean>(true);
  const [redirectError, setRedirectError] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  
  useEffect(() => {
    // Validate session before proceeding
    if (!validateSession()) {
      console.log("Invalid session detected, redirecting to home");
      navigate('/');
      return;
    }
    
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const providerParam = searchParams.get('provider');
    
    // Sanitize URL parameters
    const sanitizedToken = token ? sanitizeString(token) : null;
    const sanitizedProvider = providerParam ? sanitizeString(providerParam) : null;
    
    console.log("SortterPage - URL params received:", { token: sanitizedToken, provider: sanitizedProvider });
    
    // Validate provider parameter
    const validProviders = ['axo', 'sambla', 'sortter'];
    const targetProvider = sanitizedProvider && validProviders.includes(sanitizedProvider.toLowerCase()) 
      ? sanitizedProvider.toLowerCase() 
      : 'axo';
    
    // Track page view
    try {
      trackEvent('lainahakemus_page_view', {
        has_token: !!sanitizedToken,
        provider: targetProvider,
        auto_redirect: true
      });
    } catch (error) {
      console.log("Analytics tracking failed - likely due to cookie settings");
    }
    
    // Determine redirect URL based on provider
    let targetUrl: string;
    
    switch (targetProvider) {
      case 'sortter':
        targetUrl = getSortterRedirectUrl();
        break;
      case 'sambla':
        targetUrl = getSamblaRedirectUrl();
        break;
      case 'axo':
      default:
        targetUrl = getAxoRedirectUrl(sanitizedToken || undefined);
        break;
    }
    
    // Additional URL validation
    try {
      new URL(targetUrl);
    } catch (error) {
      console.error("Invalid redirect URL generated:", targetUrl);
      setRedirectError(true);
      setIsRedirecting(false);
      return;
    }
    
    setRedirectUrl(targetUrl);
    console.log(`SortterPage - Redirecting to ${targetProvider} with URL:`, targetUrl);
    
    // Track redirect attempt
    try {
      trackEvent('auto_redirect_external', {
        provider: targetProvider,
        token: sanitizedToken || 'none',
        url: targetUrl
      });
    } catch (error) {
      console.log("Analytics tracking failed");
    }

    // Perform redirect after short delay
    const redirectTimer = setTimeout(() => {
      try {
        console.log("SortterPage - Executing redirect to:", targetUrl);
        window.location.href = targetUrl;
      } catch (error) {
        console.error("SortterPage - Redirect failed:", error);
        setRedirectError(true);
        setIsRedirecting(false);
        toast.error("Uudelleenohjaus epäonnistui.");
      }
    }, 1000);

    // Set fallback timer
    const fallbackTimer = setTimeout(() => {
      console.log("SortterPage - Fallback timer triggered");
      setRedirectError(true);
      setIsRedirecting(false);
      toast.error("Automaattinen uudelleenohjaus epäonnistui. Käytä manuaalista nappia.");
    }, 5000);
    
    return () => {
      clearTimeout(redirectTimer);
      clearTimeout(fallbackTimer);
    };
    
  }, [location.search, trackEvent, navigate]);
  
  // Manual redirect handler
  const handleManualRedirect = () => {
    if (redirectUrl) {
      console.log("SortterPage - Manual redirect to:", redirectUrl);
      
      try {
        trackEvent('manual_redirect_click', {
          url: redirectUrl
        });
      } catch (error) {
        console.log("Analytics tracking failed");
      }
      
      window.location.href = redirectUrl;
    } else {
      // Fallback to home page
      navigate('/');
    }
  };
  
  // Try alternative provider
  const handleTryAlternative = () => {
    toast.info("Ohjataan vaihtoehtoiseen palveluun...");
    
    try {
      trackEvent('fallback_redirect', {
        to: 'axo',
        reason: 'connection_failed'
      });
    } catch (error) {
      console.log("Analytics tracking failed");
    }
    
    setTimeout(() => {
      const fallbackUrl = getAxoRedirectUrl();
      window.location.href = fallbackUrl;
    }, 500);
  };

  return (
    <>
      <Helmet>
        <title>Lainatarjoukset | Lainasampo.fi</title>
        <meta 
          name="description" 
          content="Lainahakemustasi käsitellään. Lainasampo.fi auttaa sinua löytämään edullisimman lainan."
        />
        <meta property="og:title" content="Lainatarjoukset | Lainasampo.fi" />
      </Helmet>

      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md text-center">
          {isRedirecting ? (
            <>
              <div className="flex justify-center mb-6">
                <Loader2 className="animate-spin h-10 w-10 text-blue-600" />
              </div>
              <h1 className="text-xl font-bold text-blue-700 mb-4">Hakemustasi käsitellään</h1>
              <p className="text-gray-600 mb-6">Odota hetki, sinut ohjataan lainatarjouksiin automaattisesti.</p>
              
              {redirectUrl && (
                <Button 
                  onClick={handleManualRedirect}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Jatka manuaalisesti
                </Button>
              )}
            </>
          ) : redirectError ? (
            <>
              <div className="flex justify-center mb-6">
                <AlertTriangle className="h-10 w-10 text-amber-500" />
              </div>
              <h1 className="text-xl font-bold text-blue-700 mb-4">Yhteysvirhe</h1>
              <p className="text-gray-600 mb-6">Palveluntarjoajan sivulle siirtymisessä tapahtui virhe.</p>
              <div className="flex flex-col space-y-3">
                {redirectUrl && (
                  <Button 
                    onClick={handleManualRedirect}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Yritä uudelleen
                  </Button>
                )}
                <Button 
                  onClick={handleTryAlternative}
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  Kokeile vaihtoehtoista palvelua
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default SortterPage;
